import produce from 'immer';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ActionMethod} from 'constants/AnalyticsConstants';
import {EventConstants} from 'constants/EventConstants';
import * as context from 'Context';
import {SchemaTag} from 'folder-items/FileConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import navigateToURL from 'lib/navigateToURL';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import TemplateActions from 'notebook/entry-templates/TemplateActions';
import {
  FromServerTemplate,
  FromServerTemplateVersion,
  Template,
  TemplateCollection,
  ToServerTemplate,
} from 'notebook/entry-templates/TemplateConstants';
import TemplateUpdater from 'notebook/entry-templates/TemplateUpdater';
import TemplateUtil from 'notebook/entry-templates/TemplateUtil';
import VersionedItemSubstore from 'notebook/store/VersionedItemSubstore';
import {trackUpdateStructuredTableMappingsEvent} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsAnalytics';
import {
  StructuredTableMappingsGlobalState,
  StructuredTableMappingsStateManager,
} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsState';
import getAllStructuredTablesInNote from 'notebook/structured-tables/util/getAllStructuredTablesInNote';
import {removeQueryResolutionsForTableFromSnapshotInNote} from 'notebook/tables/excess/Resolver';
import {getUpdatedNoteWithValidationErrors} from 'notebook/tables/excess/ValidationErrorHandler';
import {FocusedTableViewMode} from 'notebook/tables/TableConstants';
import {createNoteRuntimeSnapshot} from 'notebook/tables/TableStoreTransforms';
import {EntryNote, NotePart} from 'notebook/util/NoteUtilConstants';
import BaseEntryStore from 'stores/BaseEntryStore';

// Store for a collection of Entry Templates
class TemplateStore extends BaseEntryStore<
  Template,
  FromServerTemplate,
  FromServerTemplateVersion,
  'template',
  'templateId'
> {
  private collections: Array<TemplateCollection> | null = null;
  private collection: TemplateCollection | null = null;
  private collectionId: string | null = null;
  private activeTemplate: Template | null = null;

  createItemSubstore(): VersionedItemSubstore<
    Template,
    NotePart,
    FromServerTemplate,
    FromServerTemplateVersion,
    'template',
    'templateId'
  > {
    return new VersionedItemSubstore({
      options: {
        idKey: 'templateId',
        itemKey: 'template',
        getItemParts(item: Template) {
          return item.templateVersion.entryVersion.note.noteParts.map((itemPart) => ({
            itemPartId: itemPart.cid,
            itemPart,
          }));
        },
        getVersionCid(item: Template) {
          return item.templateVersion.cid;
        },
        asyncItemLoad: true,
        postSanitizeItem: this.postSanitizeItem.bind(this),
        sanitizeItem: TemplateUtil.sanitizeTemplate.bind(this),
        invertPostProcess: TemplateUtil.removeTasksFromTemplate.bind(this),
        prepareForStore: TemplateUtil.prepareForStore.bind(this),
        pickRevertableFields: TemplateUtil.pickRevertableFields.bind(this),
        doSave(id: string) {
          return TemplateActions.save(id);
        },
        enableItemPartLevelSaving: true,
      },
      events: {
        item: {
          load: {
            init: EventConstants.TEMPLATE_OPEN,
            completed: EventConstants.TEMPLATE_OPEN_COMPLETED,
            failed: EventConstants.TEMPLATE_OPEN_FAILED,
          },
          registerObjects: EventConstants.TEMPLATE_REGISTER_OBJECTS,
          close: EventConstants.TEMPLATE_CLOSE,
          rename: EventConstants.FILE_BROWSER_RENAME_FILE,
          resetToOpenVersion: EventConstants.TEMPLATE_RESET_TO_OPEN_VERSION,
          revert: EventConstants.TEMPLATE_REVERT,
          update: EventConstants.TEMPLATE_UPDATE,
          save: {
            init: EventConstants.TEMPLATE_SAVE,
            completed: EventConstants.TEMPLATE_SAVE_COMPLETED,
            failed: EventConstants.TEMPLATE_SAVE_FAILED,
          },
        },
        version: {
          load: {
            init: EventConstants.TEMPLATE_LOAD_VERSION,
            completed: EventConstants.TEMPLATE_LOAD_VERSION_COMPLETED,
            failed: EventConstants.TEMPLATE_LOAD_VERSION_FAILED,
          },
        },
        versions: {
          fetch: {
            init: EventConstants.TEMPLATE_FETCH_VERSIONS,
            completed: EventConstants.TEMPLATE_FETCH_VERSIONS_COMPLETED,
            failed: EventConstants.TEMPLATE_FETCH_VERSIONS_FAILED,
          },
        },
      },
    });
  }

  getNote(id: string): EntryNote {
    const template = this.itemSubstore.getItemUnsafe(id);
    return template.templateVersion.entryVersion.note;
  }

  postSanitizeItem(
    id: string,
    newTemplate: Template,
    oldTemplate: Template | null,
    {bumpVersion, performTableEvaluation = true}: {bumpVersion?: boolean; performTableEvaluation?: boolean}
  ): Template {
    // TODO(BNCH-30610): Remove this edge case once persist eval has been fully rolled out (when ELN_ENABLE_RUNTIME_SNAPSHOT_EVAL will always be true)
    if (!BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_EVAL) {
      // If Excess Version is null we can no-op since it signifies an empty NRS
      if (newTemplate.templateVersion.entryVersion.note.noteRuntimeSnapshot.excessVersion == null) {
        return newTemplate;
      }
      const emptySnapshot = createNoteRuntimeSnapshot();
      return {
        ...newTemplate,
        templateVersion: {
          ...newTemplate.templateVersion,
          entryVersion: {
            ...newTemplate.templateVersion.entryVersion,
            note: {
              ...newTemplate.templateVersion.entryVersion.note,
              noteRuntimeSnapshot: emptySnapshot,
            },
          },
        },
      };
    }

    if (!performTableEvaluation) {
      return newTemplate;
    }

    // We always set performTableEvaluation=true when we update structured table mappings on the template,
    // so we can skip updating the table mapping global state if performTableEvaluation=false
    if (
      oldTemplate?.templateVersion.entryVersion?.structuredTableMappings !==
      newTemplate.templateVersion.entryVersion?.structuredTableMappings
    ) {
      StructuredTableMappingsGlobalState.updateState(
        id,
        StructuredTableMappingsStateManager.setStructuredTableMappings(
          newTemplate.templateVersion.entryVersion.structuredTableMappings
        )
      );
    }

    const updater = new TemplateUpdater(
      id,
      () => this.itemSubstore.getItem(id),
      (templateVersion, _asyncMultidocTableOp) => {
        TemplateActions.update(
          id,
          {$merge: {templateVersion}},
          {bumpVersion} // TODO(BNCH-137948): Table op support for templates
        );
      }
    );
    return updater.run(newTemplate, oldTemplate);
  }

  override resetState(): void {
    this.collections = null; // list of collections (summary dictionaries) that user has access to
    this.collection = null; // active collection (full dictionary)
    this.collectionId = null;
    super.resetState();
  }

  override inStore(id: string): boolean {
    return this.itemSubstore.inStore(id);
  }

  getCollections(): Array<TemplateCollection> | undefined {
    return this.collections ?? undefined;
  }

  getCollection(): TemplateCollection | undefined {
    return this.collection ?? undefined;
  }

  /**
   * Returns the Template for the given templateId, provided it has been loaded into the store. If it has not
   * yet been loaded into the store, then this will return null.
   */
  getTemplate(templateId: string): Template | undefined {
    return this.itemSubstore.getItemState(templateId)?.template ?? undefined;
  }

  /**
   * A helper function which calls `getTemplate` but does an `assertAndContinue` that the template is not
   * null.
   */
  getTemplateUnsafe(templateId: string): Template {
    const maybeTemplate = this.getTemplate(templateId);
    assertAndContinue(maybeTemplate != null, 'Expected to find template in store.');
    return maybeTemplate!;
  }

  /**
   * Returns the most recently activated template, provided it is still in the store. If it has been removed
   * from the store, then this will return null.
   */
  getActiveTemplate(): Template | undefined {
    if (this.activeTemplate && this.inStore(this.activeTemplate.id)) {
      return this.activeTemplate;
    }
    return undefined;
  }

  syncTags(tagData: Array<{id: string; tags: Array<SchemaTag>}>): void {
    tagData.forEach((data) => {
      const {id, tags} = data;
      if (this.inStore(id)) {
        TemplateActions.setTags(id, tags);
      }
    });
  }

  override payloadHandler(action: FluxAction): boolean {
    // send analytics for table mapping changes, if needed
    if (action.actionType === EventConstants.TEMPLATE_REVERT) {
      const versionOp = action.op.templateVersion?.entryVersion.$merge;
      if (versionOp) {
        const {templateId} = action;
        const updatedMappings = versionOp.structuredTableMappings;
        if (
          updatedMappings !== StructuredTableMappingsGlobalState.getState(templateId).structuredTableMappings
        ) {
          trackUpdateStructuredTableMappingsEvent(
            ActionMethod.REVERT_NOTE,
            templateId,
            true,
            updatedMappings,
            getAllStructuredTablesInNote(versionOp.note),
            null
          );
        }
      }
    }
    // @ts-expect-error ts(2345)
    let shouldEmit = this.itemSubstore.handlePayload(action);
    switch (action.actionType) {
      case EventConstants.TEMPLATE_SAVE_COMPLETED: {
        const {templateId, cidNotePartProvenanceIdMap} = action;
        // propagate any new note part provenance IDs to the store
        const currTemplate = this.getTemplate(templateId);
        if (currTemplate == null) {
          break;
        }
        const updatedTemplate = produce(currTemplate, (draft) => {
          draft.templateVersion.entryVersion.note.noteParts.forEach((notePart) => {
            if (
              cidNotePartProvenanceIdMap &&
              cidNotePartProvenanceIdMap[notePart.cid] != null &&
              notePart.provenanceId !== cidNotePartProvenanceIdMap[notePart.cid]
            ) {
              notePart.provenanceId = cidNotePartProvenanceIdMap[notePart.cid];
            }
          });
        });
        this.itemSubstore.updateItem(templateId, updatedTemplate, {
          bumpVersion: false,
          performTableEvaluation: false,
        });
        shouldEmit = true;
        break;
      }
      case EventConstants.EXCESS_DELETE_SNAPSHOTTED_RESOLUTIONS_FOR_TABLE: {
        const {itemId, provenanceUuid, tableOp, lookupConfigIds} = action;
        const currTemplate = this.getTemplate(itemId);
        if (currTemplate == null) {
          break;
        }
        const note = currTemplate.templateVersion.entryVersion.note;
        const updatedTemplate = {
          ...currTemplate,
          templateVersion: {
            ...currTemplate.templateVersion,
            entryVersion: {
              ...currTemplate.templateVersion.entryVersion,
              note: removeQueryResolutionsForTableFromSnapshotInNote(note, provenanceUuid, lookupConfigIds),
            },
          },
        };

        this.itemSubstore.updateItem(itemId, updatedTemplate, {
          bumpVersion: true,
          multidocTableOp: {[itemId]: tableOp},
        });
        shouldEmit = true;
        break;
      }
      case EventConstants.EXCESS_FORCE_EVALUATION: {
        const {fileId} = action;
        // If the template isn't in the store anymore we can skip evaluation.
        if (!this.inStore(fileId)) {
          return true;
        }

        const {template} = this.itemSubstore.getItemStateUnsafe(fileId);

        const updater = new TemplateUpdater(
          fileId,
          () => this.itemSubstore.getItem(fileId),
          (templateVersion) => {
            TemplateActions.update(fileId, {$merge: {templateVersion}}, {bumpVersion: false});
          }
        );
        context.using({forceExcessRevaluate: true}, this, () => updater.run(template, template));

        shouldEmit = true;
        break;
      }
      case EventConstants.TEMPLATE_OPEN_COMPLETED: {
        const {templateId} = action;
        const template = this.getTemplate(templateId);
        if (template == null) {
          break;
        }
        this.collectionId = template.templateCollection.id;
        this.collection = action.templateCollection ?? template.templateCollection;
        this.activeTemplate = template;
        shouldEmit = true;
        break;
      }
      case EventConstants.TEMPLATE_DELETE_COMPLETED:
        _.defer(() => {
          assertAndContinue(
            this.collectionId != null,
            "A Template was deleted but we don't have a Collection to redirect to afterwards.  This could " +
              'happen if we never loaded the Template before deleting it.'
          );
          navigateToURL(TemplateCollectionDAO.collectionURL(this.collectionId!));
        });
        this.activeTemplate = null;
        shouldEmit = false;
        break;
      case EventConstants.TEMPLATE_COLLECTIONS_FETCH_COMPLETED: {
        const {collections} = action;
        this.collections = collections;
        shouldEmit = true;
        break;
      }
      case EventConstants.TEMPLATE_COLLECTION_FETCH:
        // If the collection is already loaded, we can keep the existing data.
        if (this.collection != null && this.collectionId === action.collectionId) {
          break;
        }
        this.collectionId = action.collectionId;
        this.collection = null;
        shouldEmit = true;
        break;
      case EventConstants.TEMPLATE_COLLECTION_FETCH_COMPLETED:
        // If a different collection has finished loading than the desired one, ignore it.
        if (this.collectionId !== action.collection.id) {
          break;
        }
        this.collection = action.collection;
        shouldEmit = true;
        break;
      case EventConstants.TEMPLATE_COLLECTION_UPDATE_COMPLETED:
        if (this.collectionId !== action.collectionId) {
          break;
        }
        // eslint-disable-next-line no-restricted-syntax
        this.collection = _.extend({}, this.collection, action.collection);
        shouldEmit = true;
        break;

      case EventConstants.TEMPLATE_COLLECTION_DELETE_COMPLETED:
        if (this.collectionId !== action.collectionId) {
          break;
        }
        this.collection = null;
        this.collectionId = null;

        // If the open template collection is deleted, we should navigate back to the
        // collections page. We defer the navigate as it will trigger another action.
        _.defer(() => {
          navigateToURL(TemplateCollectionDAO.collectionsURL());
        });
        shouldEmit = true;
        break;
      case EventConstants.FILE_TAGS_UPDATED: {
        const {tagData} = action;
        this.syncTags(tagData);
        break;
      }
      case EventConstants.ENTRY_SET_FOCUSED_TABLE: {
        const {fileId, focusedTableProvenanceUuid, focusedTableViewMode} = action;
        if (!this.inStore(fileId)) {
          break;
        }
        this.itemSubstore.mergeItem(fileId, {
          focusedTableProvenanceUuid,
          focusedTableViewMode: focusedTableViewMode ?? FocusedTableViewMode.FULLSCREEN,
        });
        shouldEmit = true;
        break;
      }
      case EventConstants.STRUCTURED_TABLE_VALIDATION_ERRORS: {
        const {entryId, validationErrorResponse} = action;
        if (!this.inStore(entryId)) {
          break;
        }
        const currTemplate = this.itemSubstore.getItemStateUnsafe(entryId).template;
        const note = currTemplate.templateVersion.entryVersion.note;
        const updatedTemplate = produce(currTemplate, (draft) => {
          // @ts-expect-error ts(2322)
          draft.templateVersion.entryVersion.note = getUpdatedNoteWithValidationErrors(
            note,
            validationErrorResponse
          );
        });
        this.itemSubstore.updateItem(entryId, updatedTemplate, {bumpVersion: true});
        shouldEmit = true;
        break;
      }
      default:
        break;
    }

    if (shouldEmit) {
      this.emitChange();
    }
    return true;
  }

  prepareForSave(id: string, template: Template): ToServerTemplate {
    const {cidIdMap} = this.itemSubstore.getItemStateUnsafe(id);
    const unsavedNoteParts = this.getUnsavedItemParts(id);
    return TemplateUtil.prepareForSave(template, cidIdMap, unsavedNoteParts);
  }

  __serverBootstrapItem(object: {id: string; item: FromServerTemplate}): void {
    this.itemSubstore.__serverBootstrapItem(object);
  }
}

const templateStore = new TemplateStore(AppDispatcher);
export default templateStore;
